import React, { forwardRef } from 'react';
import { Box, Stack, Typography, Switch, Button, CircularProgress, Alert } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

interface CreateBulkUsersDrawerContentProps {
  isSubmitting: boolean;
  selectedFile: File | null;
  onFileChange: (file: File | null) => void;
  onSubmit: (file: File, isTrial: boolean) => void;
  isTrial: boolean;
  setIsTrial: React.Dispatch<React.SetStateAction<boolean>>;
}

const CreateBulkUsersDrawerContent = forwardRef<HTMLInputElement, CreateBulkUsersDrawerContentProps>(
  ({ isSubmitting, selectedFile, onFileChange, onSubmit, isTrial, setIsTrial }, fileInputRef) => {

    const handleUploadClick = () => {
      (fileInputRef as React.RefObject<HTMLInputElement>).current?.click();
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0] || null;
      onFileChange(file);
    };

    const handleSubmit = () => {
      if (selectedFile) {
        onSubmit(selectedFile, isTrial);
      }
    };

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ pb: 4 }}>
          <Alert severity="info" icon={false} sx={{ backgroundColor: '#E5F8FE', color: '#26C6F9' }}>
            <Typography variant="subtitle2">By default, all users added via file upload are set to trial status.</Typography>
          </Alert>
        </Box>
        {/* Upload Box */}
        <Stack spacing={2}>
          <Box
            onClick={handleUploadClick}
            sx={{
              border: '2px dashed #ccc',
              borderRadius: '8px',
              p: 2,
              cursor: 'pointer',
              '&:hover': { backgroundColor: '#f9f9f9' },
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >

            <Stack direction="row" spacing={2} alignItems="center">
              {selectedFile ? (
                <>
                  <CloudUploadIcon sx={{ fontSize: 40, color: '#A8A8A8', height: '50px', width: '50px' }} />
                  <Box>
                    <Typography
                      variant="subtitle1"
                      fontWeight={500}
                      noWrap
                      sx={{
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        maxWidth: 200
                      }}
                    >
                      {selectedFile.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Click to change file
                    </Typography>
                  </Box>
                </>
              ) : (
                <>
                  <CloudUploadIcon sx={{ fontSize: 40, color: '#A8A8A8', height: '50px', width: '50px' }} />
                  <Box>
                    <Typography variant="subtitle1" fontWeight={500}>
                      Upload XLSX File
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Browse file to upload
                    </Typography>
                  </Box>
                </>
              )}
            </Stack>
            <input
              type="file"
              accept=".xlsx"
              ref={fileInputRef}
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
          </Box>
        </Stack>

        {/* Trial Mode Toggle */}
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="body2" sx={{ color: '#818181' }}>
            Set the users as <Box component="span" sx={{ fontWeight: 600 }}>“TRIAL”</Box> mode?
          </Typography>
          <Switch
            checked={isTrial}
            onChange={(e) => setIsTrial(e.target.checked)}
            disabled={isSubmitting}
          />
        </Box>

        {/* Submit Button */}
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={isSubmitting || !selectedFile}
          startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
        >
          ADD USERS
        </Button>
      </Box>
    );
  }
);

export default CreateBulkUsersDrawerContent;
